import { h, Component } from 'preact';
import Store from 'store/store';

import ScrollBox from 'components/shared/scrollBox/scrollBox';
import Button from 'components/bolt/button/button';
import { smoothScrollTo } from 'utils/smoothScroll';
import { joinClasses, accessNested, isEmptyObject, arraysEqual } from 'utils/utils';
import formatDateTime from 'utils/formatDateTime';
// import Reset from 'components/bolt/icons/reset';
// import { removeClass, addClass } from 'utils/utils';
import Chevron from 'components/bolt/icons/chevron';

import s from 'components/bolt/showtimes/dateScroller.sss';

export default class DateScroller extends Component {
	constructor(props) {
		super(props);
		this.dateClickHandlers = {};
		this.datesKeyDown = this.datesKeyDown.bind(this);
		this.scrollRight = this.scrollRight.bind(this);
		this.scrollLeft = this.scrollLeft.bind(this);
		this.today = formatDateTime(new Date(), 'yyyy-MM-dd');
		this.scrollSelectedDateToView = this.scrollSelectedDateToView.bind(this);
		this.textDirection = accessNested(props, 'appData.styling.fontVariables.textDirection', '');
		this.$dateElems = [];
	}

	componentDidMount() {
		this.setDatesInScroller(this.props.displayedDates);
	}

	componentDidUpdate(prevProps, prevState) {
		const { selected, active, available, loading } = this.props.displayedDates;
		const prevDates = prevProps.displayedDates;

		const displayedDatesChanged = !arraysEqual(available, prevDates.available) ||
			!arraysEqual(selected, prevDates.selected) ||
			(active && !arraysEqual(active, prevDates.active)) ||
			(loading !== prevDates.loading && (prevDates.loading === false || undefined));

		const modesChanged = (this.props.showtimesMode !== prevProps.showtimesMode) || (this.props.mapMode !== prevProps.mapMode);
		const inCollapsedMap = this.props.mapMode === "collapsed" && this.props.showtimesMode === 'map';
		const comingBackToListMode = this.props.showtimesMode === 'list' && prevProps.showtimesMode === 'map';

		if (this.props.theaterDateScroller && this.props.showtimesMode !== 'list') {
			this.scrollSelectedDateToView();
		}

		if (modesChanged || displayedDatesChanged) {
			this.setDatesInScroller(this.props.displayedDates);
			if (!this.props.theaterDateScroller && (inCollapsedMap || comingBackToListMode)) {
				this.scrollSelectedDateToView();
			}
		}

		if (this.state.selectedDate !== prevState.selectedDate) {
			this.updateSelectedDateElement();
			if (this.mayNeedScroll) {
				this.scrollSelectedDateToView();
				this.mayNeedScroll = false;
			}
		}

		if (available !== prevProps.displayedDates?.available) {
			this.mayNeedScroll = true;
			this.updateSelectedDateElement();
			this.scrollSelectedDateToView();
		}
	}

	setDatesInScroller(displayedDates) {
		let datesInScroller = displayedDates.available.filter(d => d >= this.today).sort();

		if (displayedDates.active) {
			datesInScroller = datesInScroller.map((d) => [
				d,
				displayedDates.selected.indexOf(d) !== -1 || displayedDates.active.indexOf(d) !== -1
			]);
		} else {
			datesInScroller = datesInScroller.map((d) => [d, true]);
		}

		if (this.props.hideInactive) {
			datesInScroller = datesInScroller.filter(([d, isActive]) => isActive);
		}

		this.setState({
			datesInScroller,
			loading: displayedDates.loading,
			selectedDate: displayedDates.selected
		});
	}

	updateSelectedDateElement() {
		const { selectedDate } = this.state;
		this.$selectedDate = document.querySelector(`#date-${selectedDate}`);
	}

	scrollSelectedDateToView() {
		this.updateSelectedDateElement();

		const { $dates } = this;
		const { $scroller } = $dates || {};

		if ($scroller && this.$selectedDate) {
			const selectedDateRect = this.$selectedDate.getBoundingClientRect();
			const scrollBoxRect = $scroller.getBoundingClientRect();
			if ((selectedDateRect.x < scrollBoxRect.x) || (selectedDateRect.x > (scrollBoxRect.x + scrollBoxRect.width))) {
				smoothScrollTo($scroller, this.$selectedDate, 0, { direction: 'hor', offset: 8 });
			}
		}
	}

	focus() {
		if (this.$dates && this.$dates.base) {
			this.$dates.base.focus();
		}
	}

	dateClicked(date) {
		Store.emit('FILTER_SET', 'date', date);
		if (this.props.onChange) {
			setTimeout(() => this.props.onChange?.(date), 0);
		}

		if (this.props.theaterDateScroller) return;

		const inactiveDate = this.state.datesInScroller.find(([theDate, isActive]) => theDate === date && !isActive);
		if (inactiveDate) {
			smoothScrollTo(this.props.scrollElem || {});
		}
	}

	getDateClickHandler(date) {
		if (!this.dateClickHandlers[date]) {
			this.dateClickHandlers[date] = this.dateClicked.bind(this, date);
		}
		return this.dateClickHandlers[date];
	}

	datesKeyDown(e) {
		// let filters = this.state.datesInScroller;

		let offset = 0;
		if (e.key === 'ArrowRight') {
			offset = 1;
		}
		if (e.key === 'ArrowLeft') {
			offset = -1;
		}
		if (offset) {
			e.preventDefault();
			let allDates = this.state.datesInScroller;
			let firstSelectionIndex = this.state.datesInScroller.indexOf(this.state.selectedDate[0]);

			let newIndex = firstSelectionIndex + offset;
			if (firstSelectionIndex !== -1 && newIndex >= 0 && newIndex < allDates.length) {
				Store.emit('FILTER_SET', 'date', allDates[newIndex]);
			}
		}
	}

	isDefault() {
		let filters = this.props.filters;
		let selected = filters.selected;
		if (!selected) {
			return true;
		}
		let firstDate = filters.available?.date?.[0];
		return Object.keys(selected).every(filter => {
			let sel = selected[filter];
			if (!sel || !sel.length) {
				return true;
			}
			return filter === 'date' && sel.length === 1 && (sel[0] === firstDate || sel[0] === this.today);
		});
	}

	getDisplayedFilters(available) {
		return Object.keys(available).filter(e => e !== 'date' && e !== 'exhibitor' && available[e] && available[e].length);
	}

	renderDates() {
		const { selectedTheater, showtimesMode } = this.props;
		let dateTimeLocale = accessNested(this.props, 'appData.copy.baseCopy.$DATETIME_LOCALE');
		const accessibilityCopy = accessNested(this.props, 'appData.copy.accessibilityCopy', {});
		const fontStyles = accessNested(this.props, 'appData.styling.fontVariables');
		const selectedTheaterDates = accessNested(Store.get(), 'showtimes.theatersDates', {});
		const datesFromFilteredFilters = accessNested(Store.get(), 'showtimes.filteredFilters.dates', []);
		const dates = this.state.datesInScroller || [];
		const selected = this.state.selectedDate || [];

		return dates.filter(([dateStr, isActive]) => {
			if (isEmptyObject(selectedTheaterDates) || showtimesMode === 'list') return true;
			if (selectedTheaterDates[selectedTheater.id].includes(dateStr)) return true;
		}).map(([dateStr, isActive], index) => {
			let parts = dateStr.split('-');
			let date = new Date(+parts[0], parts[1] - 1, +parts[2]);
			let isSelected = selected.includes(dateStr);
			const dateNotInCurrentFilter = !datesFromFilteredFilters.includes(dateStr);

			return (
				<div
					key={dateStr}
					id={`date-${dateStr}`}
					className={joinClasses(s.date, isActive && s.active)}
					ref={e => this.$dateElems[index] = e}
				>
					<Button
						disabled={isSelected || dateNotInCurrentFilter}
						class={joinClasses({ s, dateButton: true, selected: isSelected, active: isActive, greyedOut: dateNotInCurrentFilter })}
						trigger={this.getDateClickHandler(dateStr)}
						tabIndex="-1"
						ariaLabel={accessibilityCopy.$DATE_SELECTION_ARIA.replace('<option>', dateStr)}
					>
						<span class={joinClasses(s.month, isSelected && s.selected, fontStyles.filterMenuMonth)}>{formatDateTime(date, 'MMM', dateTimeLocale)}</span>
						<span class={joinClasses(s.dom, isSelected && s.selected, fontStyles.filterMenuDate)}>{formatDateTime(date, 'dd', dateTimeLocale)}</span>
						<span class={joinClasses(s.day, isSelected && s.selected, fontStyles.filterMenuDayActive)}>{formatDateTime(date, 'ddd', dateTimeLocale)}</span>
					</Button>
				</div>
			);
		});
	}

	scrollRight() {
		const { $dates } = this;
		const { $scroller } = $dates || {};

		if ($scroller) {
			const scrollTo = $scroller.scrollLeft + 80;
			smoothScrollTo($scroller, scrollTo, 300, { direction: 'hor' });
		}
	}

	scrollLeft() {
		const { $dates } = this;
		const { $scroller } = $dates || {};

		if ($scroller) {
			const scrollTo = $scroller.scrollLeft - 80;
			smoothScrollTo($scroller, scrollTo, 300, { direction: 'hor' });
		}
	}

	render(props) {
		// let filters = props.filters;
		// let filtersCopy = accessNested(props, 'appData.copy.baseCopy.$FILTERS') || 'Filters';
		const accessibilityCopy = accessNested(props, 'appData.copy.accessibilityCopy', {});
		const fontStyles = accessNested(props, 'appData.styling.fontVariables');

		const showScrollBtnsOnMobile = accessNested(props.showtimesAppData, 'showScrollBtnsOnMobile', false);

		const isRTL = fontStyles.textDirection === 'rtl';
		// const moreThanOneDate = this.state.datesInScroller && this.state.datesInScroller.length > 1;

		return (
			<div ref={e => this.$container = e}
				class={joinClasses(
					s.container,
					(this.props.isSticked || this.props.showtimesMode === 'map') && s.sticked,
					this.props.theaterDateScroller && s.selectedTheaterView,
					this.props.class
				)}
			>
				<div class={s.content}>
					<ScrollBox
						class={joinClasses(s.dates, showScrollBtnsOnMobile && s.showScrollBtnsOnMobile)}
						device="mobile"
						tabIndex="0"
						onKeyDown={this.datesKeyDown}
						ref={e => this.$dates = e}
						hideScrollerBar={true}
						scrollableLeftClass={!isRTL ? s.hasMoreLeft : s.hasMoreRight}
						scrollableRightClass={!isRTL ? s.hasMoreRight : s.hasMoreLeft}
						isRTL={isRTL}
					>
						{this.renderDates()}
						<div className={s.filler} />
					</ScrollBox>
					<div class={joinClasses(s.scrollButtonContainer, showScrollBtnsOnMobile && s.showScrollBtnsOnMobile)}>
						<div
							class={joinClasses(s.more, s.left)}
							onClick={this.scrollLeft}
							role="button"
							ariaLabel={accessibilityCopy.$LEFT_ARROW_DATES_ARIA}
						>
							<Chevron ariaLabel={accessibilityCopy.$LEFT_ARROW_DATES_ARIA} />
						</div>
						<div className={s.separator} />
						<div
							class={joinClasses(s.more, s.right)}
							onClick={this.scrollRight}
							role="button"
							ariaLabel={accessibilityCopy.$RIGHT_ARROW_DATES_ARIA}
						>
							<Chevron ariaLabel={accessibilityCopy.$RIGHT_ARROW_DATES_ARIA} />
						</div>
					</div>
				</div>
			</div>
		);
	}
}
